import PrintIcon from "@mui/icons-material/Print";
import { Observer } from "mobx-react-lite";
import React, { type ComponentType, forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InventoryRecord } from "../../../stores/definitions/InventoryRecord";
import useStores from "../../../stores/use-stores";
import { match } from "../../../util/Util";
import PrintDialog from "../Print/PrintDialog";
import ContextMenuAction, { type ContextMenuRenderOptions } from "./ContextMenuAction";

type PrintBarcodeActionArgs = {
  as: ContextMenuRenderOptions;
  closeMenu: () => void;
  disabled: string;
  selectedResults: Array<InventoryRecord>;
};

const PrintBarcodeAction: ComponentType<PrintBarcodeActionArgs> = forwardRef<
  React.ElementRef<typeof ContextMenuAction>,
  PrintBarcodeActionArgs
>(({ as, closeMenu, disabled, selectedResults }, ref) => {
  const { t } = useTranslation("inventory");
  const { searchStore, trackingStore } = useStores();

  const [showPrintDialog, setShowPrintDialog] = useState(false);

  const disabledHelp = match<void, string>([
    [() => disabled !== "", disabled],
    [
      () => selectedResults.some((r) => r.recordType === "sampleTemplate"),
      t("contextMenu.printBarcode.templatesHaveNoBarcodes"),
    ],
    [
      () => selectedResults.some((r) => !r.canRead),
      t("contextMenu.printBarcode.noPermission", {
        count: selectedResults.length,
      }),
    ],
    [() => searchStore.activeResultIsBeingEdited, t("contextMenu.printBarcode.recordBeingEdited")],
    [() => true, ""],
  ])();

  const handleOpen = () => {
    setShowPrintDialog(true);
    trackingStore.trackEvent("user:open:printDialog:inventoryContextMenu");
  };

  return (
    <Observer>
      {() => (
        <ContextMenuAction
          onClick={handleOpen}
          icon={<PrintIcon />}
          label={t("contextMenu.actions.printBarcode")}
          disabledHelp={disabledHelp}
          as={as}
          ref={ref}
        >
          {showPrintDialog && (
            <PrintDialog
              showPrintDialog={showPrintDialog}
              onClose={() => setShowPrintDialog(false)}
              itemsToPrint={selectedResults}
              closeMenu={closeMenu}
            />
          )}
        </ContextMenuAction>
      )}
    </Observer>
  );
});

PrintBarcodeAction.displayName = "PrintBarcodeAction";
export default PrintBarcodeAction;
